/**
 * Convert an Outlook SafeLinks login URL (or a direct login URL) to a localhost URL.
 *
 * Examples:
 *  SafeLink ->
 *    https://eur01.safelinks.protection.outlook.com/?url=https%3A%2F%2Fprod.example.com%2Faccounts%2Flogin%2Fabcd%3Fnext%3D%2F&data=...
 *  Result ->
 *    http://localhost:4200/accounts/login/abcd?next=/
 *
 *  Direct link ->
 *    https://prod.example.com/accounts/login/abcd?next=/
 *  Result ->
 *    http://localhost:4200/accounts/login/abcd?next=/
 */

const DEFAULT_LOCAL_ORIGIN = "http://localhost:4200";

function safeDecode(value: string): string {
  let current = value;
  for (let i = 0; i < 3; i++) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
}

function extractTargetUrl(raw: string): string {
  // If it's an Outlook SafeLink it will have a 'url' query param
  try {
    const parsed = new URL(raw);
    const encodedInner = parsed.searchParams.get("url");
    if (encodedInner) {
      return safeDecode(encodedInner);
    }
    // No inner param -> treat as already the target
    return raw;
  } catch {
    // Not a valid URL -> return as-is (will fail later if unusable)
    return raw;
  }
}

function rewriteOriginToLocal(targetUrl: string, localOrigin: string): string {
  let urlObj: URL;
  try {
    urlObj = new URL(targetUrl);
  } catch {
    // If missing protocol, try to force https
    urlObj = new URL("https://" + targetUrl.replace(/^\/+/, ""));
  }

  const local = new URL(localOrigin);
  // Preserve path/search/hash
  return `${local.origin}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
}

/**
 * Public API.
 * @param loginLink SafeLinks URL or direct login URL
 * @param localOrigin override origin (defaults to http://localhost:4200)
 * @returns transformed localhost URL
 */
export function getCorrectLoginLink(
  loginLink: string,
  localOrigin: string = DEFAULT_LOCAL_ORIGIN
): string {
  if (!loginLink) throw new Error("No login link provided");
  const target = extractTargetUrl(loginLink);
  return rewriteOriginToLocal(target, localOrigin);
}
